import {ErrorHandler, ModuleWithProviders, NgModule} from '@angular/core';
import {SocketClusterService} from './services/socket-cluster.service';
import {RelevantToolURLGenerator} from './services/relevant-tool-url-generator.service';
import {FileManagementService} from './services/file-management.service';
import {ParticleService} from './services/particle.service';
import {SnackBarService} from './services/snack-bar.service';
import {ParticleApiService} from './services/particle-api.service';
import {AnalyticsService} from './services/analytics.service';
import {TimeZoneService} from './services/time-zone.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {SmartGridService} from './services/smart-grid.service';
import {SmartGridApiService} from './services/smart-grid-api.service';
import {CommandService} from './services/command.service';
import {HTTP_INTERCEPTORS, HttpClientModule} from '@angular/common/http';
import {AuthApiService} from './services/auth-api.service';
import {AuthInterceptor, TempAccessTokenService} from './interceptors/auth.interceptor';
import {TrackingIdInterceptor} from './interceptors/tracking-id.interceptor';
import {SessionCookieInterceptor} from './interceptors/session-cookie.interceptor';
import {AuthGuard, BasketAuthGuard} from './services/auth-guard.service';
import {SecurityApiService} from './services/security-api.service';
import {GraphQLService} from './services/graphql.service';
import {ScrumboardService} from './services/scrumboard.service';
import {ChaptersApiService} from './services/chapters-api.service';
import {WorkflowService} from './services/workflow.service';
import {VirtualGridApiService} from './services/virtual-grid-api.service';
import {QueryApiService} from './services/query-api.service';
import {DynamicContentViewerApiService} from './services/dynamic-content-viewer-api.service';
import {TaskManagerApiService} from '@features/task-manager/services/task-manager-api.service';
import {SharedUIService} from './services/sharedui.service';
import {BrowserModule} from '@angular/platform-browser';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {CloudSearchService} from './services/cloud-search.service';
import {ViewSynchronizerService} from '@shared/components/platform-sidebar/store/view-synchronizer.service';
import {DialogService} from '@shared/services/dialog-service';
import {HtmlConverterService} from '@shared/services/html-converter-api.service';
import {TaskManagerService} from '@features/task-manager/services/task-manager.service';
import {ShareItemService} from '@shared/services/share-item.service';
import {SmartGridV2ApiService} from '@shared/services/smart-grid-v2-api.service';
import {ParticleCacheService} from './services/particle-cache.service';
import {CollaborationApiService} from '@features/task-manager/collaboration/store/collaboration-api.service';
import {TitleShareService} from './services/title.service';
import {NewsApiService} from './services/news-api.service';
import {OnlineActivityService} from './services/online-activity.service';
import {CollaborationHashtagService} from '@features/task-manager/collaboration/store/collaboration-hashtag.service';
import {RightSidebarWaitScreenService} from './services/right-sidebar-waitcreen.service';
import {CollaborationService} from '@features/task-manager/collaboration/store/collaboration.service';
import {PlatformSidebarService} from '@shared/components/platform-sidebar/store/platform-sidebar.service';
import {PlatformSidebarRoutesForNavigation} from '@shared/components/platform-sidebar/store/platform-sideber-routes-for-navigration.service';
import {CanDeactivateComponentGuard} from './services/can-deactivate-component-guard.service';
import {HallmarkTaggingService} from './services/hallmark-tagging.service';
import {FieldHistoryService} from './services/field-history.service';
import {SignalrService} from './services/signalr-service';
import {DetailsInstructionService} from '@shared/services/details-instruction-service';
import {ProgressBarService} from './services/progress-bar.service';
import {ActivityFilterService} from '@features/task-manager/collaboration/services/activity-filter.service';
import {EatFlowChartService} from '@features/data-survey/services/eat-flow-chart.service';
import {PlatformApiProvider} from '@shared/services/platform-api-provider';
import {PlatformQueryService} from './services/platform-query.service';
import {PdfParserService} from '@shared/services/pdf-parser.service';
import {PdfFormEditorService} from '@shared/services/pdf-form-editor.service';
import {DashboardService} from '@core/services/dashboard.service';
import {FileOrFolderSelectorService} from '@shared/services/file-or-folder-selector.service';
import {ItpDesktopService} from '@core/services/itpdesktopservices/itp-desktop.service';
import {ItpDesktopUtilityService} from '@core/services/itpdesktopservices/itp-desktop-utility.service';
import {DynamicFormService} from "@shared/services/dynamic-form.service";
import {HttpBackendClient} from '@core/services/http-backend.service';
import { ItpDesktopEventDispatchService } from '@core/services/itpdesktopservices/Itp-desktop-event-dispatcher.service';
import {WorkflowUtilityService} from '@core/services/workflow-utility.service';
import {RightSideMagicBarStateService} from '@core/services/right-side-magic-bar/right-side-magic-bar-state.service';
import { BulkFileUploadService } from './services/bulk-file-upload.service';
import {SpeechService} from '@shared/services/speech.service';
import {ObservationErrorHandler} from '@core/services/observation-error-handler';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
  ],
  exports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
  ],
})
export class CoreModule {

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [SocketClusterService,
        HtmlConverterService,
        RelevantToolURLGenerator,
        FileManagementService,
        BulkFileUploadService,
        ParticleService,
        SnackBarService,
        TempAccessTokenService,
        ParticleApiService,
        AnalyticsService,
        TimeZoneService,
        FuseSplashScreenService,
        SmartGridService,
        SmartGridApiService,
        SmartGridV2ApiService,
        CommandService,
        // ITP2-14341: ahead of AuthInterceptor, so a request from a session whose cookie was removed is dropped unsigned.
        {provide: HTTP_INTERCEPTORS, useClass: SessionCookieInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true},
        {provide: HTTP_INTERCEPTORS, useClass: TrackingIdInterceptor, multi: true},
        AuthApiService,
        AuthGuard,
        BasketAuthGuard,
        CanDeactivateComponentGuard,
        SecurityApiService,
        GraphQLService,
        ScrumboardService,
        ChaptersApiService,
        NewsApiService,
        WorkflowService,
        VirtualGridApiService,
        QueryApiService,
        DynamicContentViewerApiService,
        TaskManagerApiService,
        CollaborationApiService,
        SharedUIService,
        CloudSearchService,
        ViewSynchronizerService,
        TaskManagerService,
        EatFlowChartService,
        ShareItemService,
        DialogService,
        ParticleCacheService,
        TitleShareService,
        OnlineActivityService,
        CollaborationHashtagService,
        RightSidebarWaitScreenService,
        CollaborationService,
        PlatformSidebarService,
        PlatformSidebarRoutesForNavigation,
        HallmarkTaggingService,
        FieldHistoryService,
        SignalrService,
        DetailsInstructionService,
        ProgressBarService,
        ActivityFilterService,
        PlatformApiProvider,
        PlatformQueryService,
        PdfParserService,
        PdfFormEditorService,
        DashboardService,
        FileOrFolderSelectorService,
        ItpDesktopService,
        ItpDesktopUtilityService,
        ItpDesktopEventDispatchService,
        DynamicFormService,
        HttpBackendClient,
        WorkflowUtilityService,
        RightSideMagicBarStateService,
        SpeechService,
        // THE OBSERVATION'S FRONTEND ERROR CAPTURE (MISN-E-71-06, ADR 0070). Replaces Angular's
        // default handler and DELEGATES TO IT first, so the console error every developer relies
        // on is untouched; it only adds a second reader — the observed execution's own trail —
        // and only while one is current.
        {provide: ErrorHandler, useClass: ObservationErrorHandler}
      ]
    };
  }
}
